import { twMerge } from '@shared/themes';
import type { ComponentProps } from 'react';
import { LegacySitePurchasesNotification } from '../../lottery-purchases/PurchasedTickets/components/LegacySitePurchasesNotification';
import type { NextDrawItem } from '../../lottery-results/components/NextDraw/types';
import { SecondaryHeaderContainer } from '../../SecondaryHeaderContainer/SecondaryHeaderContainer';
import { LotteryCategoryContent } from '../categories/LotteryCategoryContent/LotteryCategoryContent';
import { LotteryCategoryResultsCount } from '../categories/LotteryCategoryResultsCount/LotteryCategoryResultsCount';
import type { LotteriesCategory } from '../types';

type LotteryCategoryLayoutContainerProps = ComponentProps<'div'>;

type LotteryCategoryLayoutProps = LotteryCategoryLayoutContainerProps & {
  category: LotteriesCategory;
  isLoading: boolean;
  lotteriesData: NextDrawItem[];
  skeletonItems?: number;
  emptyMessage?: React.ReactNode;
};

/**
 * Layout for the lottery category pages: a styled grid container (overriding the global
 * max-container width) with a header showing the results count and a content grid of
 * lottery pods. Category components only need to fetch data and derive `lotteriesData`,
 * then hand it to this component.
 */
function LotteryCategoryLayout({ className, category, isLoading, lotteriesData, skeletonItems, emptyMessage, ...props }: LotteryCategoryLayoutProps) {
  return (
    <div className={twMerge('ll-grid-container [--ll-container-max-width:880px]', className)} {...props}>
      <SecondaryHeaderContainer namespace="lotteries.categories" title={category} className="ll-container-full" data-track-container="Category">
        <div className="ll-container-inner paragraph-s-strong text-foreground-subtle ml-10 mt-3">
          <LotteryCategoryResultsCount isLoading={isLoading} resultsCount={lotteriesData.length} />
        </div>
      </SecondaryHeaderContainer>
      <div className="ll-container-inner pb-6 md:pb-8 grid grid-cols-1 md:grid-cols-2 gap-2.5" data-testid="category-results" data-track-container="Results">
        <LotteryCategoryContent isLoading={isLoading} lotteries={lotteriesData} skeletonItems={skeletonItems} emptyMessage={emptyMessage} />
      </div>
      {category === 'all_lottos' && (
        <LegacySitePurchasesNotification variant="categories" className="ll-container-inner mb-10" data-track-type="Legacy Bets" data-track-message="Not Available" />
      )}
    </div>
  );
}

export { LotteryCategoryLayout };
export type { LotteryCategoryLayoutContainerProps };
